import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";

const useStyles = makeStyles((theme) => ({
  board: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    overflowX: "auto",
    paddingBottom: theme.spacing(2),
  },
  column: {
    backgroundColor: "#F5F7FA",
    borderRadius: 8,
    flex: "0 0 280px",
    maxWidth: 280,
    padding: theme.spacing(2),
  },
  highlighted: {
    backgroundColor: "#EAF6F6",
    borderLeft: "4px solid #4FA3A5",
  },
  columnTitle: {
    fontWeight: 600,
  },
  columnCount: {
    color: theme.palette.text.secondary,
    display: "block",
    marginBottom: theme.spacing(1),
  },
  dropZone: {
    borderRadius: 6,
    minHeight: 48,
    transition: "background-color 0.2s",
  },
  dropZoneActive: {
    backgroundColor: "#E3ECF7",
  },
  card: {
    paddingTop: theme.spacing(1),
  },
  footer: {
    marginTop: theme.spacing(1.5),
  },
  empty: {
    color: theme.palette.text.secondary,
  },
}));

export type KanbanColumn<T> = {
  key: string;
  title: string;
  cards: T[];
};

export type KanbanMove = {
  cardKey: string;
  fromColumn: string;
  fromIndex: number;
  toColumn: string;
  toIndex: number;
};

export type DragHandle = DraggableProvidedDragHandleProps | null | undefined;

interface Props<T> {
  columns: KanbanColumn<T>[];
  getCardKey: (card: T) => string;
  renderCard: (card: T, column: KanbanColumn<T>, dragHandle: DragHandle) => React.ReactNode;
  renderColumnHeader?: (column: KanbanColumn<T>) => React.ReactNode;
  renderColumnFooter?: (column: KanbanColumn<T>) => React.ReactNode;
  onMoveCard?: (move: KanbanMove) => void;
  isCardDragDisabled?: (card: T) => boolean;
  dragDisabled?: boolean;
  highlightedColumnKey?: string;
  emptyMessage?: string;
}

function KanbanBoard<T>({
  columns,
  getCardKey,
  renderCard,
  renderColumnHeader,
  renderColumnFooter,
  onMoveCard,
  isCardDragDisabled,
  dragDisabled = false,
  highlightedColumnKey,
  emptyMessage = "Nothing to show.",
}: Props<T>) {
  const classes = useStyles();
  const canDrag = !!onMoveCard && !dragDisabled;

  if (columns.length === 0) {
    return (
      <Typography className={classes.empty} variant="body2">
        {emptyMessage}
      </Typography>
    );
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !onMoveCard) {
      return;
    }
    onMoveCard({
      cardKey: result.draggableId,
      fromColumn: result.source.droppableId,
      fromIndex: result.source.index,
      toColumn: result.destination.droppableId,
      toIndex: result.destination.index,
    });
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box className={classes.board} data-test-id="kanban-board">
        {columns.map((column) => (
          <Box
            className={`${classes.column} ${
              column.key === highlightedColumnKey ? classes.highlighted : ""
            }`}
            data-test-id={`kanban-column-${column.key}`}
            key={column.key}
          >
            <Typography className={classes.columnTitle} variant="subtitle2">
              {column.title}
            </Typography>
            {renderColumnHeader && renderColumnHeader(column)}
            <Typography className={classes.columnCount} variant="caption">
              {column.cards.length}
            </Typography>

            <Droppable droppableId={column.key} isDropDisabled={!canDrag}>
              {(dropProvided, dropSnapshot) => (
                <div
                  className={`${classes.dropZone} ${
                    dropSnapshot.isDraggingOver ? classes.dropZoneActive : ""
                  }`}
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                >
                  {column.cards.map((card, index) => {
                    const cardCanDrag =
                      canDrag && !(isCardDragDisabled && isCardDragDisabled(card));
                    return (
                      <Draggable
                        draggableId={getCardKey(card)}
                        index={index}
                        isDragDisabled={!cardCanDrag}
                        key={getCardKey(card)}
                      >
                        {(dragProvided) => (
                          <div
                            className={classes.card}
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                          >
                            {renderCard(
                              card,
                              column,
                              cardCanDrag ? dragProvided.dragHandleProps : null
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>

            {renderColumnFooter && (
              <Box className={classes.footer}>{renderColumnFooter(column)}</Box>
            )}
          </Box>
        ))}
      </Box>
    </DragDropContext>
  );
}

export default KanbanBoard;
